import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { TOAST_MESSAGES } from "~/constants/messages";
import { UserApi } from "~/functions/api/user";
import EyeIcon from "~/images/eye.svg?react";
import EyeOffIcon from "~/images/eye-off.svg?react";
import type { UpdateUser, UserData } from "~/interface/user";
import { setCurrentUserData } from "~/slices/auth";
import type { RootState } from "~/store";
import styles from "./index.module.scss";

// 文字制限の定数
const CHARACTER_LIMITS = {
  firstName: 50,
  lastName: 50,
  displayName: 50,
};

const UserSettings: React.FC = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const { currentUserInfo, currentUser } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  // Password change (frontend only, shown for local login users)
  const [pwdCurrent, setPwdCurrent] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdConfirm, setPwdConfirm] = useState("");
  const [showPwdCurrent, setShowPwdCurrent] = useState(false);
  const [showPwdNew, setShowPwdNew] = useState(false);
  const [showPwdConfirm, setShowPwdConfirm] = useState(false);
  const passwordResetUrl = import.meta.env.VITE_AZURE_PASSWORD_UPDATE as string | undefined;

  // 文字数チェック
  const lastNameCount = useMemo(() => lastName.length, [lastName]);
  const firstNameCount = useMemo(() => firstName.length, [firstName]);
  const displayNameCount = useMemo(() => displayName.length, [displayName]);

  // 文字数制限を超えているかのチェック
  const hasExceededLimits = useMemo(() => {
    return (
      lastNameCount > CHARACTER_LIMITS.lastName ||
      firstNameCount > CHARACTER_LIMITS.firstName ||
      displayNameCount > CHARACTER_LIMITS.displayName
    );
  }, [lastNameCount, firstNameCount, displayNameCount]);

  // バックエンドから返る想定の認証手段（メールは常に読み取り専用のため未使用）
  const authProviders = (currentUserInfo as any)?.auth_providers as string[] | undefined;
  const hasLocalLogin = !!authProviders?.includes("password");

  // Password strength estimation (simple heuristic)
  const evaluateStrength = (value: string) => {
    const lengthOK = value.length >= 8;
    const types = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/];
    const variety = types.reduce((acc, re) => acc + (re.test(value) ? 1 : 0), 0);
    let score = 0;
    if (lengthOK) score++;
    if (variety >= 2) score++;
    if (variety >= 3 && value.length >= 12) score++;
    const label = score === 0 ? "弱い" : score === 1 ? "やや弱い" : score === 2 ? "普通" : "強い";
    return { score, label };
  };
  const strength = useMemo(() => evaluateStrength(pwdNew), [pwdNew]);
  const passwordMeetsPolicy = useMemo(() => {
    const lengthOK = pwdNew.length >= 8;
    const types = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/];
    const variety = types.reduce((acc, re) => acc + (re.test(pwdNew) ? 1 : 0), 0);
    return lengthOK && variety >= 3; // 8文字以上 & 種類3つ以上
  }, [pwdNew]);

  const handleUpdate = async () => {
    if (hasExceededLimits) {
      toast.error(TOAST_MESSAGES.USER_SETTINGS.CHAR_LIMIT_EXCEEDED);
      return;
    }

    const updateBody: UpdateUser = {
      first_name: firstName,
      last_name: lastName,
      display_name: displayName,
    };

    const userApi = new UserApi();

    const response = await userApi.update(currentUser.organization_id, updateBody);
    if (response !== null) {
      toast.success(TOAST_MESSAGES.USER_SETTINGS.SAVE_SUCCESS);
      const updatedUserData: UserData = {
        last_name: lastName,
        first_name: firstName,
        display_name: displayName,
      };
      dispatch(setCurrentUserData(updatedUserData));
    }
  };

  useEffect(() => {
    setFirstName(currentUser.first_name || "");
    setLastName(currentUser.last_name || "");
    setDisplayName(currentUser.display_name || "");
    setEmail(currentUserInfo?.email || "");
  }, [currentUser, currentUserInfo?.email]); // userが変わるたびに実行される

  // ページ内メニューのタブ状態は廃止（ユーザー設定のみ表示）

  if (!currentUser) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <h1 className={styles["pageTitle"]}>アカウント設定</h1>
      <div className={styles["settings-container"]}>
        <div className={styles["wrapper"]}>
          <ul className={styles["list"]}>
            <li className={"knowledge_selected__BCVy2"}>ユーザー設定</li>
          </ul>
          <div className={styles["content"]}>
            {
              <>
                <h2>ユーザー設定</h2>

                <div className={styles["formsContainer"]}>
                  <div className={styles["name-container"]}>
                    <div className={styles["form-group"]}>
                      <label>
                        <span>姓</span>
                        <span className={styles["labelRight"]}>
                          <span
                            className={`${styles["char-counter"]} ${lastNameCount > CHARACTER_LIMITS.lastName ? styles["exceeded"] : ""}`}
                          >
                            {lastNameCount}/{CHARACTER_LIMITS.lastName}
                          </span>
                        </span>
                      </label>
                      <input
                        type="text"
                        value={lastName}
                        placeholder="ノベル"
                        onChange={(e) => setLastName(e.target.value)}
                        maxLength={CHARACTER_LIMITS.lastName}
                      />
                    </div>
                    <div className={styles["form-group"]}>
                      <label>
                        <span>名</span>
                        <span className={styles["labelRight"]}>
                          <span
                            className={`${styles["char-counter"]} ${firstNameCount > CHARACTER_LIMITS.firstName ? styles["exceeded"] : ""}`}
                          >
                            {firstNameCount}/{CHARACTER_LIMITS.firstName}
                          </span>
                        </span>
                      </label>
                      <input
                        type="text"
                        value={firstName}
                        placeholder="筋太郎"
                        onChange={(e) => setFirstName(e.target.value)}
                        maxLength={CHARACTER_LIMITS.firstName}
                      />
                    </div>
                  </div>

                  <div className={styles["form-group"]}>
                    <label className={styles["label-with-badge"]}>
                      <span className={styles["label-text"]}>表示名</span>
                      <span className={styles["required-badge"]}>必須</span>
                      <span
                        className={`${styles["char-counter"]} ${displayNameCount > CHARACTER_LIMITS.displayName ? styles["exceeded"] : ""}`}
                      >
                        {displayNameCount}/{CHARACTER_LIMITS.displayName}
                      </span>
                    </label>
                    <input
                      type="text"
                      value={displayName}
                      placeholder="表示名"
                      onChange={(e) => setDisplayName(e.target.value)}
                      maxLength={CHARACTER_LIMITS.displayName}
                      required
                      data-testid="user-name"
                    />
                  </div>

                  <div className={styles["form-group"]}>
                    <label>メールアドレス</label>
                    <input type="email" value={email} readOnly />
                  </div>
                </div>

                <div className={styles["actions"]}>
                  <button
                    className={styles["save-button"]}
                    onClick={handleUpdate}
                    disabled={hasExceededLimits}
                    data-testid="save-profile"
                  >
                    保存
                  </button>
                </div>

                {hasLocalLogin && (
                  <>
                    <h2>パスワード変更</h2>
                    <div className={styles["passwordContainer"]}>
                      <div className={styles["form-group"]}>
                        <label>
                          <span>現在のパスワード</span>
                        </label>
                        <div className={styles["inputWithIcon"]}>
                          <input
                            type={showPwdCurrent ? "text" : "password"}
                            value={pwdCurrent}
                            onChange={(e) => setPwdCurrent(e.target.value)}
                            placeholder="現在のパスワード"
                          />
                          <button
                            type="button"
                            className={styles["toggleEyeButton"]}
                            aria-label={showPwdCurrent ? "パスワードを非表示" : "パスワードを表示"}
                            onClick={() => setShowPwdCurrent((v) => !v)}
                          >
                            {showPwdCurrent ? <EyeOffIcon /> : <EyeIcon />}
                          </button>
                        </div>
                      </div>

                      <div className={styles["form-group"]}>
                        <label>
                          <span>新しいパスワード</span>
                        </label>
                        <div className={styles["inputWithIcon"]}>
                          <input
                            type={showPwdNew ? "text" : "password"}
                            value={pwdNew}
                            onChange={(e) => setPwdNew(e.target.value)}
                            placeholder="新しいパスワード"
                          />
                          <button
                            type="button"
                            className={styles["toggleEyeButton"]}
                            aria-label={showPwdNew ? "パスワードを非表示" : "パスワードを表示"}
                            onClick={() => setShowPwdNew((v) => !v)}
                          >
                            {showPwdNew ? <EyeOffIcon /> : <EyeIcon />}
                          </button>
                        </div>

                        <div className={styles["strengthWrap"]}>
                          <div className={styles["strengthMeter"]}>
                            <div
                              className={styles["strengthBar"]}
                              style={{
                                width: `${(strength.score + 1) * 25}%`,
                                background:
                                  strength.score <= 0
                                    ? "#DC2626"
                                    : strength.score === 1
                                      ? "#F59E0B"
                                      : strength.score === 2
                                        ? "#10B981"
                                        : "#059669",
                              }}
                            />
                          </div>
                          <div className={styles["strengthText"]}>強度: {strength.label}</div>
                          <div className={styles["requirements"]}>
                            8文字以上、英大文字・英小文字・数字・記号のうち3種類以上を含めてください。
                          </div>
                        </div>
                      </div>

                      <div className={styles["form-group"]}>
                        <label>
                          <span>新しいパスワード（確認）</span>
                        </label>
                        <div className={styles["inputWithIcon"]}>
                          <input
                            type={showPwdConfirm ? "text" : "password"}
                            value={pwdConfirm}
                            onChange={(e) => setPwdConfirm(e.target.value)}
                            placeholder="もう一度入力"
                          />
                          <button
                            type="button"
                            className={styles["toggleEyeButton"]}
                            aria-label={showPwdConfirm ? "パスワードを非表示" : "パスワードを表示"}
                            onClick={() => setShowPwdConfirm((v) => !v)}
                          >
                            {showPwdConfirm ? <EyeOffIcon /> : <EyeIcon />}
                          </button>
                        </div>
                      </div>

                      <div className={styles["actions"]}>
                        <button
                          className={styles["save-button"]}
                          onClick={() => {
                            if (!pwdCurrent) {
                              toast.error(TOAST_MESSAGES.USER_SETTINGS.CURRENT_PASSWORD_REQUIRED);
                              return;
                            }
                            if (!passwordMeetsPolicy) {
                              toast.error(
                                TOAST_MESSAGES.USER_SETTINGS.PASSWORD_REQUIREMENTS_NOT_MET,
                              );
                              return;
                            }
                            if (pwdNew !== pwdConfirm) {
                              toast.error(TOAST_MESSAGES.USER_SETTINGS.PASSWORD_MISMATCH);
                              return;
                            }
                            toast.success(
                              "パスワード変更リクエストを送信しました（フロント実装のみ）",
                            );
                          }}
                          disabled={!pwdCurrent || !passwordMeetsPolicy || pwdNew !== pwdConfirm}
                        >
                          変更
                        </button>
                      </div>

                      {passwordResetUrl && (
                        <p className={styles["passwordResetHelp"]}>
                          <a
                            className={styles["password-link"]}
                            href={passwordResetUrl}
                            target="_blank"
                            rel="noreferrer noopener"
                          >
                            パスワードを忘れた場合はこちら
                          </a>
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/** 外部サービス連携状況（要望によりコメントアウト）
              <div className={styles['externalSection']}>
                <h2 className={styles['section-title']}>外部サービス連携状況</h2>
                <p className={styles['externalDescription']}>
                  外部サービスと連携することで簡単にログインできます。
                </p>
                <div className={styles['externalCard']}>
                  {['google','microsoft'].map((p) => {
                    const connected = p === 'google';
                    const providerLabel = p === 'google' ? 'Google' : 'Microsoft';
                    const baseEmail = (currentUserInfo as any)?.email || '';
                    const displayEmail = baseEmail;
                    return (
                      <div className={styles['externalRow']} key={p}>
                        <div className={styles['providerCell']}>
                          <span className={styles['providerIcon']} aria-hidden="true">
                            {p === 'google' ? (
                              <img
                                src={`${import.meta.env.BASE_URL || '/'}assets/google-logo.svg`}
                                alt="Google"
                                width={20}
                                height={20}
                                onError={(e) => {
                                  (e.currentTarget as HTMLImageElement).src = 'https://reqxignaadb2cformsprod.blob.core.windows.net/signinorsignup/Google_Logo.svg';
                                }}
                              />
                            ) : (
                              <MicrosoftLogo />
                            )}
                          </span>
                          {connected && <span>{displayEmail}</span>}
                        </div>
                        <div className={styles['statusCell']}>{connected ? '連携中' : '未連携'}</div>
                        <div className={styles['actionCell']}>
                          <UIButton
                            styleType="white-feedback"
                            label={connected ? '解除する' : '連携する'}
                            onClick={() => {
                              const msg = connected ? `${providerLabel} 連携の解除は準備中です` : `${providerLabel} 連携は準備中です`;
                              alert(msg);
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              **/}
              </>
            }
          </div>
        </div>
      </div>
    </>
  );
};

export default UserSettings;
